import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import styles from "./searchableSelect.module.scss";

export interface SearchableSelectOption {
  value: string;
  label: string;
  /** Optional group header label. Options sharing the same group render as a
   *  contiguous block under one header — the caller controls group AND item
   *  order by how it orders `options`; this component only detects "group
   *  changed from the previous item" and inserts a header at render time. */
  group?: string;
}

/**
 * Type-to-filter single-select combobox. A text input that filters a dropdown
 * by label or value; keyboard-navigable (↑/↓/Enter/Escape), closes on outside
 * click. Use in place of a native <select> when the option list is long enough
 * that scanning it is painful (cultures, timezones, …).
 *
 * The menu is `position: fixed`, positioned from the input's viewport rect and
 * rendered inline (a descendant of the input). Fixed positioning means it is
 * NOT clipped by a scrollable ancestor (e.g. a modal body with overflow), and
 * rendering inline keeps it in the parent <dialog>'s top layer rather than
 * hiding behind it. Reposition on scroll/resize while open.
 *
 * Extracted from the scheduled-export form so the export dialog and schedule
 * form share one implementation.
 */
export function SearchableSelect({
  options,
  value,
  onChange,
  disabled,
  placeholder,
  ariaLabel,
  className,
}: {
  options: SearchableSelectOption[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  ariaLabel?: string;
  /** Extra class on the wrapper, e.g. to let it fill a flex form row. */
  className?: string;
}) {
  const selected = options.find((o) => o.value === value);
  const displayLabel = selected?.label ?? value;
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number; width: number } | null>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    // Also match the value with separators removed, so "deat" finds "de-AT".
    const qNoSep = q.replace(/[-_]/g, "");
    return options.filter((o) => {
      const label = o.label.toLowerCase();
      const val = o.value.toLowerCase();
      return label.includes(q) || val.includes(q) || val.replace(/[-_]/g, "").includes(qNoSep);
    });
  }, [options, query]);

  // Position the fixed menu under the input; keep it glued on scroll/resize.
  useLayoutEffect(() => {
    if (!open) { setCoords(null); return; }
    const update = () => {
      const el = wrapRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setCoords((prev) => {
        const next = { top: r.bottom + 4, left: r.left, width: r.width };
        if (prev && prev.top === next.top && prev.left === next.left && prev.width === next.width) return prev;
        return next;
      });
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const choose = useCallback((v: string) => { onChange(v); setOpen(false); setQuery(""); }, [onChange]);

  if (disabled) {
    return <input type="text" className={styles.input} value={displayLabel} disabled readOnly aria-label={ariaLabel} />;
  }

  return (
    <div ref={wrapRef} className={className ? `${styles.combo} ${className}` : styles.combo}>
      <input
        type="text"
        className={styles.input}
        value={open ? query : displayLabel}
        placeholder={placeholder ?? "Search…"}
        aria-label={ariaLabel}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); setActive(0); }}
        onFocus={() => { setOpen(true); setQuery(""); setActive(0); }}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); setActive((a) => Math.min(a + 1, filtered.length - 1)); }
          else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
          else if (e.key === "Enter") { if (open && filtered[active]) { e.preventDefault(); choose(filtered[active]!.value); } }
          else if (e.key === "Escape") { setOpen(false); setQuery(""); }
        }}
      />
      {open && coords && (
        <div
          className={styles.comboMenu}
          role="listbox"
          style={{ position: "fixed", top: coords.top, left: coords.left, width: coords.width, zIndex: 1000 }}
        >
          {filtered.length === 0 ? (
            <div className={styles.comboEmpty}>No matches</div>
          ) : (
            filtered.slice(0, 200).map((o, i, arr) => (
              <div key={o.value}>
                {o.group && o.group !== arr[i - 1]?.group && (
                  <div className={styles.comboGroupHeader}>{o.group}</div>
                )}
                <div
                  role="option"
                  aria-selected={o.value === value}
                  className={[styles.comboOption, i === active ? styles.comboOptionActive : "", o.value === value ? styles.comboOptionSelected : ""].filter(Boolean).join(" ")}
                  onMouseEnter={() => setActive(i)}
                  onMouseDown={(e) => { e.preventDefault(); choose(o.value); }}
                  title={o.label}
                >
                  {o.label}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
